import React from 'react';
import PropTypes from 'prop-types';
import {Space, Tooltip} from 'antd';
import {useTranslation} from 'react-i18next';
import {StyledMissingErpTabIcon, StyledMissingErpTabIconsSpace} from './StyledMissingErpTab';
import {
  StyledCheckedCircleIconContainer,
  StyledJournalEntryTabContainer
} from '../../../NetSuiteJournalEntriesPage/JournalEntriesTabs/StyledJournalEntriesTabs';
import TableFilters from '../../TableFilters';
import EmptyOrTable from '../../../../EmptyOrTable';
import {accountingHelpers} from '../../../../../utils/helpers';
import {CheckIcon, DownloadIcon, TrashIcon} from '../../../../../icons';
import {useBillsTabState} from '../../hooks';

const dateColumnProps = accountingHelpers.getDateColumnProps();

const MissingErpTab = ({
  data = [],
  empty,
  loading = false,
  onFilter,
  onRowUpdate,
  onRowDelete,
  onRowDownload,
  tableFiltersProps,
  tableProps,
  ...rest
}) => {
  const [t] = useTranslation('main');

  const {
    customSegments,
    listItemOptions,
    taxCodeOptions,
    handleOnFilter,
    query,
  } = useBillsTabState(onFilter);

  const getTableInput = ({ defaultValue, propName, record }) =>
    accountingHelpers.getTableInput({
      defaultValue,
      propName,
      record,
      t,
      onChange: (value) => onRowUpdate?.({ id: record.id, data: { [propName]: value } }),
    })

  const getSelect = ({ defaultValue, levels, options, propName, record, ...rest }) =>
    accountingHelpers.getTableSelect({
      defaultValue,
      options,
      propName,
      record,
      t,
      onSelect: (value) => onRowUpdate?.({ id: record.id, data: accountingHelpers.getUpdateData({levels, propName, value, entry: record}) }),
      ...rest
    });

  const handleRowDelete = (e, id) => {
    e.preventDefault();
    onRowDelete?.(e, id);
  }

  const handleDownload = (e, id) => {
    e.preventDefault();
    onRowDownload?.(e, id);
  }

  const getVendor = (defaultValue, record) =>
    accountingHelpers.getVendorName({exist: Boolean(record?.vendor?.vendor_id), data: record, t});

  const getInvoiceNumber = (value = '', record) => {
    const isReadyToSync = record?.is_ready_to_sync;

    return (
      <StyledMissingErpTabIconsSpace className='d-flex' size='middle' wrap={false}>
        <Space size='small' wrap={false}>
          {isReadyToSync && (
            <Tooltip title={t('readyToSync')}>
              <StyledCheckedCircleIconContainer>
                <CheckIcon />
              </StyledCheckedCircleIconContainer>
            </Tooltip>
          )}
          <span className='no-wrap'>{value}</span>
        </Space>
        <Space className='ant-table-cell-actions' size='small' wrap={false}>
          <Tooltip title={t('delete')}>
            <StyledMissingErpTabIcon onClick={(e) => handleRowDelete?.(e, record.id)}>
              <TrashIcon />
            </StyledMissingErpTabIcon>
          </Tooltip>
          <Tooltip title={t('download')}>
            <StyledMissingErpTabIcon onClick={(e) => handleDownload?.(e, record.id)}>
              <DownloadIcon />
            </StyledMissingErpTabIcon>
          </Tooltip>
          {accountingHelpers.getInvoiceLinkIcon({id: record?.backend_invoice_id, t})}
        </Space>
      </StyledMissingErpTabIconsSpace>
    )
  }

  const columns = [
    {
      dataIndex: 'custbody_nexil_numb_vendorbillrefnum',
      title: `${t('invoice')} ${t('number')}`,
      render: getInvoiceNumber
    },
    {
      ...dateColumnProps,
      dataIndex: 'invoice_date',
      title: `${t('invoice')} ${t('date')}`,
      render: accountingHelpers.getBillInvoiceDate,
    },
    {
      dataIndex: 'id',
      title: t('invoice'),
      render: (value) => accountingHelpers.renderInvoicePreview(value, 'accounting')
    },
    { ...dateColumnProps, dataIndex: 'due_date', title: `${t('due')} ${t('date')}` },
    {
      align: 'right',
      dataIndex: 'total_amount',
      render: accountingHelpers.getBillAmount,
      title: t('amount'),
    },
    { dataIndex: 'uploaded_by', title: t('uploadedBy') },
    {
      dataIndex: 'memo',
      render: (defaultValue, record) => getTableInput({ defaultValue, propName: 'memo', record }),
      title: t('memo')
    },
    { dataIndex: 'vendor_id', title: t('vendor'), render: getVendor },
    {
      dataIndex: 'tax_code',
      render: (_, record) => getSelect({ defaultValue: accountingHelpers.getBillTaxCode(record), record, options: taxCodeOptions, propName: 'tax_code' }),
      title: t('taxCode'),
    },
    {
      dataIndex: 'list_item',
      render: (_, record) => getSelect({ defaultValue: accountingHelpers.getBillListItem(record), record, options: listItemOptions, propName: 'line_item', placeholderPropName: 'listItem' }),
      title: t('listItem'),
    },
    ...accountingHelpers.getCustomSegmentColumns({columns: customSegments, getSelect, linesPropName: 'item'})
  ];

  return (
    <StyledJournalEntryTabContainer
      {...rest}
      size='large'
      direction='vertical'
    >
      <TableFilters
        {...tableFiltersProps}
        onFilter={handleOnFilter}
      />
      <EmptyOrTable
        data={data}
        empty={empty}
        loading={loading}
        query={query}
        columns={columns}
        tableProps={tableProps}
      />
    </StyledJournalEntryTabContainer>
  );
}

MissingErpTab.propTypes = {
  columns: PropTypes.array,
  data: PropTypes.array,
  empty: PropTypes.any,
  loading: PropTypes.bool,
  onFilter: PropTypes.func,
  onRowUpdate: PropTypes.func,
  tableFiltersProps: PropTypes.any,
  tableProps: PropTypes.object,
}

export default MissingErpTab;
